import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { PageShell, PageHero } from "@/components/PageShell";
import { getGoogleReviews, type GoogleReview } from "@/lib/google-reviews.functions";

const reviewsQueryOptions = queryOptions({
  queryKey: ["google-reviews"],
  queryFn: () => getGoogleReviews(),
  staleTime: 60 * 60 * 1000,
});

export const Route = createFileRoute("/testimonials")({
  head: () => ({
    meta: [
      { title: "Patient Testimonials | Live Google Reviews | N S Physiotherapy" },
      {
        name: "description",
        content:
          "Read verified Google reviews from patients treated at N S Physiotherapy in Centurion. Real feedback, updated live from Google.",
      },
      { property: "og:title", content: "Patient Testimonials, N S Physiotherapy" },
      { property: "og:description", content: "Verified Google reviews from real patients." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://nsphysiotherapy.co.za/testimonials" },
    ],
    links: [{ rel: "canonical", href: "https://nsphysiotherapy.co.za/testimonials" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(reviewsQueryOptions),
  component: TestimonialsPage,
  errorComponent: ({ error }) => (
    <PageShell>
      <PageHero eyebrow="Testimonials" title="Reviews are temporarily unavailable" />
      <div className="container-page py-10 text-foreground/70">
        <p>We couldn't load Google reviews right now. Please try again shortly.</p>
        <p className="mt-2 text-sm text-foreground/50">{(error as Error).message}</p>
      </div>
    </PageShell>
  ),
});

function Stars({ rating }: { rating: number }) {
  const full = Math.round(rating);
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} viewBox="0 0 20 20" className={`h-4 w-4 ${i < full ? "text-amber-500" : "text-foreground/15"}`} fill="currentColor" aria-hidden>
          <path d="M10 1.5l2.7 5.47 6.03.88-4.36 4.25 1.03 6.01L10 15.27 4.6 18.11l1.03-6.01L1.27 7.85l6.03-.88L10 1.5z" />
        </svg>
      ))}
    </div>
  );
}

function GoogleG({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}

function ReviewCard({ r }: { r: GoogleReview }) {
  return (
    <article className="flex h-full flex-col rounded-2xl border border-border bg-white p-6 shadow-sm">
      <div className="flex items-start gap-3">
        {r.photo ? (
          <img src={r.photo} alt={r.author} referrerPolicy="no-referrer" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 font-semibold text-primary">
            {r.author.charAt(0)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <a href={r.authorUri} target="_blank" rel="noopener noreferrer" className="truncate font-semibold text-primary hover:underline">
              {r.author}
            </a>
            <GoogleG className="h-3.5 w-3.5 shrink-0" />
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Stars rating={r.rating} />
            <span className="text-xs text-foreground/60">{r.relativeTime}</span>
          </div>
        </div>
      </div>
      <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-foreground/80">{r.text}</p>
      <div className="mt-4 flex items-center gap-1.5 text-xs text-foreground/50">
        <GoogleG className="h-3 w-3" />
        <span>Posted on Google</span>
      </div>
    </article>
  );
}

function TestimonialsPage() {
  const { data } = useSuspenseQuery(reviewsQueryOptions);
  const leaveReviewUrl = `https://search.google.com/local/writereview?placeid=ChIJFQkCHXhllR4Ri3Sk02mjxTI`;

  return (
    <PageShell>
      <PageHero
        eyebrow="Patient Testimonials"
        title="What our patients say"
        subtitle="Verified reviews pulled live from our Google Business Profile, updated straight from Google."
      />

      <section className="container-page py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <GoogleG className="h-8 w-8" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-foreground/50">Reviews from Google</div>
              <div className="mt-1 flex items-center gap-3">
                <span className="font-display text-3xl font-bold text-primary">{data.rating.toFixed(1)}</span>
                <Stars rating={data.rating} />
                <span className="text-sm text-foreground/60">Based on {data.totalRatings} Google reviews</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={leaveReviewUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
              Leave a Google review
            </a>
            <a href={data.placeUri} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              See all on Google
            </a>
          </div>
        </div>

        {data.reviews.length === 0 ? (
          <p className="mt-10 text-center text-foreground/60">No reviews to display yet.</p>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {data.reviews.map((r) => (
              <ReviewCard key={r.id} r={r} />
            ))}
          </div>
        )}

        <p className="mt-8 text-center text-xs text-foreground/50">
          Google reviews are the property of their respective authors. Data cached hourly from Google Places.
        </p>

        <div className="mt-10 text-center">
          <Link to="/booking" className="btn-primary">Book your appointment</Link>
        </div>
      </section>
    </PageShell>
  );
}
